import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function InvoiceCopyList({ onAdd, onEdit, onDelete }) {
    const { hasPermission } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (user) setUserRole(user.role);
    }, []);

    const fetchInvoiceCopies = async (search = '', page = 1, limit = 10) => {
        try {
            setLoading(true);
            const result = await fetchApi(`/invoice-copy?search=${search}&page=${page}&limit=${limit}`);
            if (result.invoiceCopies && Array.isArray(result.invoiceCopies)) {
                setRows(result.invoiceCopies);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            } else if (Array.isArray(result)) {
                setRows(result);
                setTotalEntries(result.length);
                setTotalPages(Math.max(1, Math.ceil(result.length / limit)));
            }
        } catch (error) {
            console.error('Fetch Error:', error);
            if (error.status !== 403) toast.error('Error fetching invoice copies');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchInvoiceCopies(searchQuery, currentPage, itemsPerPage);
        }, 200);
        return () => clearTimeout(delay);
    }, [searchQuery, currentPage, itemsPerPage]);

    const columns = [
        ...(JSON.parse(sessionStorage.getItem('user'))?.role === 'SUPER_ADMIN' ? [{ key: 'companyName', label: 'COMPANY', render: (item) => item.Company?.company_name || item.companyId?.companyName || item.companyName || item.company_name || '-' }] : []),
        { key: 'invoiceCopyName', label: 'COPY NAME' },
        {
            key: 'actions',
            label: 'ACTIONS',
            render: (item) => (
                <div className="list-page-action-icons">
                    {hasPermission('Invoice Copy', 'update') && (
                        <button
                            type="button"
                            className="list-page-icon-btn"
                            title="Edit"
                            onClick={() => onEdit(item)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {hasPermission('Invoice Copy', 'delete') && (
                        <button
                            type="button"
                            className="list-page-icon-btn"
                            title="Delete"
                            onClick={() => onDelete(item._id)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Invoice Copy List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('Invoice Copy', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={onAdd}>
                            ADD COPY
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                searchBar={
                    <SearchBar
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search copies..."
                    />
                }
            />
        </div>
    );
}

export default InvoiceCopyList;
